import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  TableChart as TableChartIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface WorksheetInfo {
  name: string;
  index: number;
  visible: boolean;
  usedRange?: string | null;
}

interface ConstructionInfo {
  constructionId: number;
  constructionName: string;
  fileName: string;
  fileSize: number;
  worksheetCount: number;
  status: string;
  worksheets: WorksheetInfo[];
}

interface IdentifyDocumentRequest {
  document_identifier: string;
  description?: string;
  primary_worksheet_name: string;
  alt_worksheet_names?: string[];
}

const steps = [
  'Review Worksheets',
  'Identify Document',
  'Confirmation'
];

interface DocumentIdentificationWizardProps {
  constructionId: string;
  onComplete?: (documentId: number) => void;
  onCancel?: () => void;
}

export const DocumentIdentificationWizard: React.FC<DocumentIdentificationWizardProps> = ({
  constructionId,
  onComplete,
  onCancel
}) => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Construction and worksheet data
  const [constructionInfo, setConstructionInfo] = useState<ConstructionInfo | null>(null);
  
  // Form state
  const [documentIdentifier, setDocumentIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [primaryWorksheet, setPrimaryWorksheet] = useState('');
  const [alternativeWorksheets, setAlternativeWorksheets] = useState<string[]>([]);
  
  // Validation errors
  const [identifierError, setIdentifierError] = useState('');
  const [primaryWorksheetError, setPrimaryWorksheetError] = useState('');

  // Fetch construction info and worksheets
  useEffect(() => {
    const fetchConstructionInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authenticatedFetch(
          `/api/appraisals/construction/xlsx/${constructionId}/sheets`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response) {
          throw new Error('No response from server');
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Error: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          setConstructionInfo(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch construction info');
        }
      } catch (err) {
        console.error('Error fetching construction info:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch construction info');
      } finally {
        setLoading(false);
      }
    };

    if (constructionId) {
      fetchConstructionInfo();
    }
  }, [authenticatedFetch, constructionId]);

  const validateDocumentIdentifier = (identifier: string): boolean => {
    if (!identifier.trim()) {
      setIdentifierError('Document identifier is required');
      return false;
    }
    
    if (identifier.length > 50) {
      setIdentifierError('Document identifier must be less than 50 characters');
      return false;
    }
    
    if (!/^[a-zA-Z_]+$/.test(identifier)) {
      setIdentifierError('Document identifier can only contain letters and underscores');
      return false;
    }
    
    setIdentifierError('');
    return true;
  };

  const validatePrimaryWorksheet = (worksheet: string): boolean => {
    if (!worksheet.trim()) {
      setPrimaryWorksheetError('Primary worksheet is required');
      return false;
    }
    
    setPrimaryWorksheetError('');
    return true;
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Validate form before proceeding
      const isIdentifierValid = validateDocumentIdentifier(documentIdentifier);
      const isPrimaryValid = validatePrimaryWorksheet(primaryWorksheet);
      
      if (!isIdentifierValid || !isPrimaryValid) {
        return;
      }
    }
    
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAlternativeWorksheetToggle = (worksheetName: string) => {
    setAlternativeWorksheets(prev => {
      if (prev.includes(worksheetName)) {
        return prev.filter(name => name !== worksheetName);
      } else if (prev.length < 3) {
        return [...prev, worksheetName];
      }
      return prev;
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const request: IdentifyDocumentRequest = {
        document_identifier: documentIdentifier.trim(),
        description: description.trim() || undefined,
        primary_worksheet_name: primaryWorksheet,
        alt_worksheet_names: alternativeWorksheets.length > 0 ? alternativeWorksheets : undefined
      };

      const response = await authenticatedFetch(
        `/api/appraisals/construction/xlsx/${constructionId}/identify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Success! Navigate to the document details page or call onComplete
        if (onComplete) {
          onComplete(result.data.documentId);
        } else {
          navigate(`/admin/deliverables/doc-id-system/documents/${result.data.documentId}`);
        }
      } else {
        throw new Error(result.message || 'Failed to identify document');
      }
    } catch (err) {
      console.error('Error identifying document:', err);
      setError(err instanceof Error ? err.message : 'Failed to identify document');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!constructionInfo) {
    return (
      <Alert severity="error">
        Failed to load construction information. Please try again.
      </Alert>
    );
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Excel Worksheets
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review the worksheets in your Excel workbook before identifying documents.
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                File Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {constructionInfo.constructionName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>File:</strong> {constructionInfo.fileName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Worksheets:</strong> {constructionInfo.worksheetCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Status:</strong> <Chip label={constructionInfo.status} size="small" />
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Typography variant="subtitle1" gutterBottom>
              Available Worksheets
            </Typography>
            <List>
              {constructionInfo.worksheets.map((worksheet, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <TableChartIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={worksheet.name}
                    secondary={`Index: ${worksheet.index}${worksheet.visible ? ' • Visible' : ' • Hidden'}`}
                  />
                  {worksheet.visible && (
                    <Chip label="Visible" color="success" size="small" />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Identify Document
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Provide information about the document you want to extract from this workbook.
            </Typography>

            <TextField
              fullWidth
              label="Document Identifier"
              value={documentIdentifier}
              onChange={(e) => {
                setDocumentIdentifier(e.target.value);
                if (identifierError) validateDocumentIdentifier(e.target.value);
              }}
              error={!!identifierError}
              helperText={identifierError || 'Use only letters and underscores (e.g., cost_breakdown, revenue_analysis)'}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
              helperText="Brief description of what this document contains"
            />

            <FormControl 
              fullWidth 
              margin="normal" 
              error={!!primaryWorksheetError}
              required
            >
              <InputLabel>Primary Worksheet</InputLabel>
              <Select
                value={primaryWorksheet}
                onChange={(e) => {
                  setPrimaryWorksheet(e.target.value);
                  if (primaryWorksheetError) validatePrimaryWorksheet(e.target.value);
                }}
                label="Primary Worksheet"
              >
                {constructionInfo.worksheets
                  .filter(ws => ws.visible)
                  .map((worksheet) => (
                    <MenuItem key={worksheet.name} value={worksheet.name}>
                      {worksheet.name}
                    </MenuItem>
                  ))}
              </Select>
              {primaryWorksheetError && (
                <FormHelperText>{primaryWorksheetError}</FormHelperText>
              )}
            </FormControl>

            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                Alternative Worksheets (Optional - up to 3)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select additional worksheets that might contain the same type of data.
              </Typography>
              
              <Box display="flex" flexWrap="wrap" gap={1}>
                {constructionInfo.worksheets
                  .filter(ws => ws.visible && ws.name !== primaryWorksheet)
                  .map((worksheet) => (
                    <Chip
                      key={worksheet.name}
                      label={worksheet.name}
                      clickable
                      onClick={() => handleAlternativeWorksheetToggle(worksheet.name)}
                      color={alternativeWorksheets.includes(worksheet.name) ? 'primary' : 'default'}
                      variant={alternativeWorksheets.includes(worksheet.name) ? 'filled' : 'outlined'}
                    />
                  ))}
              </Box>
              
              {alternativeWorksheets.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Selected alternatives: {alternativeWorksheets.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirm Document Identification
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review the document information before submitting.
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Document Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Identifier:</strong> {documentIdentifier}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Primary Worksheet:</strong> {primaryWorksheet}
                    </Typography>
                  </Grid>
                  {description && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Description:</strong> {description}
                      </Typography>
                    </Grid>
                  )}
                  {alternativeWorksheets.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>Alternative Worksheets:</strong> {alternativeWorksheets.join(', ')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Alert severity="info" sx={{ mt: 2 }}>
              After confirmation, the document will be created and you can proceed to define data sets within it.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                      color: completed || active ? 'white' : 'grey.600'
                    }}
                  >
                    {completed ? (
                      <CheckCircleIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <Typography variant="caption">{index + 1}</Typography>
                    )}
                  </Box>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="space-between">
          <Box>
            {onCancel && (
              <Button onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
          </Box>
          
          <Box>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <PlaylistAddCheckIcon />}
              >
                {submitting ? 'Creating...' : 'Create Document'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};